import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Card, Grid, Box, Button } from '@mui/material';
import { GeneralDatePicker, GeneralSelectMenu, GeneralTimePicker, GeneralUpload } from 'src/components/general';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import { networkCreateRtmp, networkGetRtmpServerList, networkUpdateRtmp } from 'src/network/rtmp';
import { useQueryClient } from '@tanstack/react-query';
import { RTMP_LIST_VOD } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

const RtmpForm = () => {
  const queryClient = useQueryClient();
  const { modal, closeModal } = useFormsModalStore();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [rtmpServerList, setRtmpServerList] = useState([]);
  const [selectedRtmpServer, setSelectedRtmpServer] = useState('');

  const rtmpDetails = modal?.data;
  const isEditMode = !!rtmpDetails;

  const [date, setDate] = useState(rtmpDetails?.RtmpStartDateTime ? new Date(rtmpDetails?.RtmpStartDateTime) : new Date());
  const [time, setTime] = useState(rtmpDetails?.RtmpStartDateTime ? new Date(rtmpDetails?.RtmpStartDateTime) : new Date());

  const defaultValues = getDefaultValues(rtmpDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const onSubmit = async (values, event) => {
    const dateISO = new Date(date).toISOString();
    const timeISO = new Date(time).toISOString();

    const datePart = dateISO.split('T')[0];
    const timePart = timeISO.split('T')[1];

    const finalDateTime = `${datePart}T${timePart}`;

    const PAYLOAD = {
      AppID: +projectId,
      RtmpRtmpServerID: selectedRtmpServer,
      RtmpLabel: values?.RtmpLabel,
      RtmpSourceLink: values?.RtmpSourceLink,
      RtmpRotation: values?.RtmpRotation ? 1 : 0,
      RtmpStartDateTime: finalDateTime,
      RtmpDestinationLinks: values?.RtmpDestinationLinks, // todo
      RtmpDestinationKeys: values?.RtmpDestinationKeys, // todo
      RtmpForceTermination: 0,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateRtmp(rtmpDetails?.RtmpID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پخش زنده موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries({ queryKey: [RTMP_LIST_VOD] });
          }
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateRtmp(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('پخش زنده موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          setSelectedRtmpServer(rtmpServerList[0]?.id);
          reset();
          await queryClient.invalidateQueries({ queryKey: [RTMP_LIST_VOD] });
        } else {
          enqueueSnackbar('با مشکل مواجه شد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onGetRtmpServerList = () => {
    networkGetRtmpServerList()
      .then((res) => {
        const rtmpServers = generateSelectMenuItems(res?.data?.items, 'RtmpServerLabel', 'RtmpServerID');
        setRtmpServerList(rtmpServers);
        setSelectedRtmpServer(rtmpServers?.RtmpRtmpServerID || rtmpServers?.[0]?.id); // todo
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    onGetRtmpServerList();
  }, []);

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField name="RtmpLabel" type="text" label="عنوان" />

                <GeneralUpload
                  label="لینک مرجع"
                  id="rtmp_source_link"
                  name="RtmpSourceLink"
                  afterChangeAction={(res) => {
                    setValue('RtmpSourceLink', res);
                    trigger('RtmpSourceLink');
                  }}
                />

                <RHFTextField name="RtmpDestinationLinks" type="text" label="لینک های مقصد" />

                <RHFTextField name="RtmpDestinationKeys" type="text" label="کلیدهای مقصد" />

                <GeneralDatePicker name="RtmpStartDateTime" label="تاریخ شروع" value={date} onChangeValue={setDate} />

                <GeneralTimePicker name="RtmptimeTime" label="ساعت شروع" value={time} onChangeValue={setTime} />

                <GeneralSelectMenu
                  label="نوع سرور"
                  name="RtmpRtmpServerID"
                  options={rtmpServerList}
                  value={selectedRtmpServer}
                  onChangeValue={setSelectedRtmpServer}
                />
                <RHFSwitch name="RtmpRotation" labelPlacement="end" label="قابلیت گردش" />
              </Box>

              <div className="flex items-center gap-4 mt-52">
                <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                  ذخیره
                </LoadingButton>
                <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                  {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                </LoadingButton>
                <Button onClick={closeModal} variant="contained" color="error">
                  انصراف
                </Button>
              </div>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </>
  );
};

export default RtmpForm;
